import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen, ChevronLeft, Copy, Save, Printer, Edit3,
  CheckCircle2, CheckCheck, AlertCircle, AlertTriangle, Info,
  Lightbulb, Users, BookMarked, GraduationCap, Clock,
  FileText, ClipboardList, Monitor, RotateCcw, PlusCircle,
  Pencil, XCircle, Layers, Video, MousePointer2, Sparkles,
  ShieldCheck, PenLine, RefreshCw, Building2, Eye, EyeOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useResources, type SavedResource } from './resources-context';
import { SaveStatusIndicator } from './SaveStatusIndicator';
import { useProfile } from './ProfileContext';
import {
  assembleAiLesson,
  lessonToText,
  type LessonInputs,
  type GeneratedLesson,
  type CurriculumSource,
  type ValidationSectionResult,
} from './lesson-engine';
import {
  generateLessonAI, checkAlignmentAI, type AlignmentCheckResult,
  checkPacingAI, type PacingCheckResult, type PacingSectionSummary,
  SubscriptionRequiredError,
} from './lesson-api';
import { useBilling } from './BillingContext';
import { CurriculumFlow } from './CurriculumFlow';
import { StandardsPicker, PickedStandardBadge } from './StandardsPicker';
import { formatStandardForField, type Standard } from './standards-data';
import { type TeachBudThemeId, getTheme } from './theme-engine';
import { ThemePicker } from './ThemePicker';
import { BrandingPanel } from './BrandingPanel';
import { defaultBranding, hasBranding, type BrandingData } from './branding';
import { BrandingHeaderRow } from './BrandingHeaderRow';
import { useDefaultThemes } from './use-default-themes';
import { lessonContentKey } from './lesson-blueprint';
import { patchPresentationSlides } from './presentation-patch';
import type { GeneratedPresentation } from './presentation-engine';
import { GenerationProgress } from './GenerationProgress';

// ── Local types ───────────────────────────────────────────────────────────────
interface LessonErrors {
  skillToTeach?: string;
  grade?: string;
  subject?: string;
  duration?: string;
}

// ── Toast notification ────────────────────────────────────────────────────────
interface Toast { id: number; message: string; type: 'success' | 'error' }

function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  function show(message: string, type: Toast['type'] = 'success') {
    const id = ++counter.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  }

  return { toasts, show };
}

function ToastContainer({ toasts }: { toasts: Toast[] }) {
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] flex flex-col gap-2 pointer-events-none print:hidden">
      <AnimatePresence>
        {toasts.map(t => (
          <motion.div key={t.id}
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className={`flex items-center gap-2.5 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold text-white ${
              t.type === 'success' ? 'bg-slate-900' : 'bg-red-600'
            }`}>
            {t.type === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
            {t.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ── Confirmation dialog ───────────────────────────────────────────────────────
function ConfirmDialog({
  open, message, onConfirm, onCancel,
}: { open: boolean; message: string; onConfirm: () => void; onCancel: () => void }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 print:hidden">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full z-10">
        <p className="text-slate-800 font-semibold text-center mb-1">Start a New Lesson?</p>
        <p className="text-slate-500 text-sm text-center mb-6">{message}</p>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 rounded-xl border-slate-200" onClick={onCancel}>
            Cancel
          </Button>
          <Button className="flex-1 rounded-xl bg-primary hover:bg-primary/90 text-white" onClick={onConfirm}>
            Start New
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Inline field error ────────────────────────────────────────────────────────
function FieldErr({ msg }: { msg?: string }) {
  if (!msg) return null;
  return (
    <AnimatePresence>
      <motion.p key={msg} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        className="text-xs text-red-500 mt-1.5 font-medium flex items-center gap-1">
        <AlertCircle size={11} /> {msg}
      </motion.p>
    </AnimatePresence>
  );
}

// ── Standards alignment banner ────────────────────────────────────────────────
type AlignmentState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'done'; result: AlignmentCheckResult }
  | { status: 'error' };

const ALIGNMENT_CONFIG: Record<AlignmentCheckResult['classification'], {
  bg: string; border: string; text: string; badge: string;
  icon: React.ReactNode; label: string;
}> = {
  Direct: {
    bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800',
    badge: 'bg-green-100 text-green-700',
    icon: <CheckCircle2 size={14} />, label: 'Directly aligned',
  },
  Partial: {
    bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800',
    badge: 'bg-blue-100 text-blue-700',
    icon: <Info size={14} />, label: 'Partially aligned',
  },
  Prerequisite: {
    bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800',
    badge: 'bg-amber-100 text-amber-700',
    icon: <AlertTriangle size={14} />, label: 'Prerequisite skill',
  },
  Extension: {
    bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-800',
    badge: 'bg-violet-100 text-violet-700',
    icon: <Sparkles size={14} />, label: 'Extends the standard',
  },
  Mismatch: {
    bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800',
    badge: 'bg-red-100 text-red-700',
    icon: <AlertCircle size={14} />, label: 'Possible mismatch',
  },
};

function AlignmentBanner({ state }: { state: AlignmentState }) {
  if (state.status === 'idle' || state.status === 'error') return null;

  if (state.status === 'checking') {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 mt-1.5 py-0.5">
        <svg className="animate-spin w-3 h-3 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
        Checking alignment…
      </div>
    );
  }

  const { result } = state;
  const cfg = ALIGNMENT_CONFIG[result.classification];

  return (
    <AnimatePresence>
      <motion.div
        key={result.classification}
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className={`mt-2 border rounded-xl p-3 ${cfg.bg} ${cfg.border}`}
      >
        <div className={`flex items-start gap-2.5 ${cfg.text}`}>
          <span className="flex-shrink-0 mt-0.5">{cfg.icon}</span>
          <div className="flex-1 min-w-0 space-y-0.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-block text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${cfg.badge}`}>
                {cfg.label}
              </span>
              <span className="text-xs leading-snug opacity-90">{result.reason}</span>
            </div>
            {result.suggestion && result.classification !== 'Direct' && (
              <p className="text-xs opacity-75 italic leading-snug pt-0.5">
                Suggestion: {result.suggestion}
              </p>
            )}
            {result.classification !== 'Direct' && (
              <p className="text-[11px] opacity-60 pt-0.5">
                You can still generate this lesson — this check is advisory only.
              </p>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Pacing validation ─────────────────────────────────────────────────────────

type PacingState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'done'; result: PacingCheckResult }
  | { status: 'error' };

type AdjustmentChange = {
  label: string;      // "Independent Practice" | "Guided Practice"
  fromCount: number;
  toCount: number;
};

type AdjustmentSummary = {
  changes: AdjustmentChange[];
  originalMinutes: number;
  newMinutes: number;
};

function wc(text: string | undefined): number {
  return text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
}
function ic(text: string | undefined): number {
  return text ? text.split('\n').filter(l => /^[\d•\-*]\s/.test(l.trim())).length : 0;
}

function buildPacingSummary(lesson: GeneratedLesson): Record<string, PacingSectionSummary> {
  const s: Record<string, PacingSectionSummary> = {};
  if (lesson.warmUp)
    s.warmUp              = { type: 'warm-up activity',             wordCount: wc(lesson.warmUp),            itemCount: ic(lesson.warmUp) };
  if (lesson.vocabulary)
    s.vocabulary          = { type: 'vocabulary review',            wordCount: wc(lesson.vocabulary),        itemCount: ic(lesson.vocabulary) };
  if (lesson.directInstruction)
    s.directInstruction   = { type: 'direct instruction examples',  wordCount: wc(lesson.directInstruction), itemCount: ic(lesson.directInstruction) };
  if (lesson.guidedPractice)
    s.guidedPractice      = { type: 'guided practice problems',     wordCount: wc(lesson.guidedPractice),    itemCount: ic(lesson.guidedPractice) };
  if (lesson.tieredPractice)
    s.tieredPractice      = { type: 'tiered independent practice',
      wordCount: wc([lesson.tieredPractice.belowLevel, lesson.tieredPractice.onLevel, lesson.tieredPractice.aboveLevel].join(' ')),
      itemCount: ic(lesson.tieredPractice.onLevel) };
  else if (lesson.independentPractice)
    s.independentPractice = { type: 'independent practice problems', wordCount: wc(lesson.independentPractice), itemCount: ic(lesson.independentPractice) };
  if (lesson.formativeAssessment)
    s.formativeAssessment = { type: 'formative assessment',         wordCount: wc(lesson.formativeAssessment), itemCount: ic(lesson.formativeAssessment) };
  if (lesson.exitTicket)
    s.exitTicket          = { type: 'exit ticket',                  wordCount: wc(lesson.exitTicket),        itemCount: ic(lesson.exitTicket) };
  return s;
}

/** Parse numbered list items (1. ... 2. ...) from a practice section. */
function splitNumberedItems(text: string): string[] {
  const lines = text.split('\n');
  const items: string[] = [];
  let buf: string[] = [];
  for (const line of lines) {
    if (/^\d+[.)]\s/.test(line.trim()) && buf.length > 0) {
      items.push(buf.join('\n'));
      buf = [line];
    } else {
      buf.push(line);
    }
  }
  if (buf.some(l => l.trim())) items.push(buf.join('\n'));
  return items.filter(i => i.trim());
}

const PACING_CFG: Record<'realistic' | 'tight' | 'overloaded', {
  bg: string; border: string; text: string; badgeBg: string;
  icon: React.ReactNode; label: string;
}> = {
  realistic:  { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', badgeBg: 'bg-green-100 text-green-700',  icon: <CheckCircle2  size={15} />, label: 'Pacing looks good'   },
  tight:      { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', badgeBg: 'bg-amber-100 text-amber-700',  icon: <AlertTriangle size={15} />, label: 'Pacing is tight'     },
  overloaded: { bg: 'bg-red-50',   border: 'border-red-200',   text: 'text-red-800',   badgeBg: 'bg-red-100 text-red-700',     icon: <AlertCircle   size={15} />, label: 'Lesson may run over' },
};

function PacingBanner({
  state, dismissed, onDismiss, onIncreaseDuration, onAutoAdjust,
}: {
  state: PacingState;
  dismissed: boolean;
  onDismiss: () => void;
  onIncreaseDuration: (duration: string) => void;
  onAutoAdjust: () => void;
}) {
  if (state.status === 'idle' || state.status === 'error' || dismissed) return null;

  if (state.status === 'checking') {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 print:hidden">
        <svg className="animate-spin w-3 h-3 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
        Estimating lesson pacing…
      </div>
    );
  }

  const { result } = state;
  const cfg = PACING_CFG[result.assessment];
  const isRealistic = result.assessment === 'realistic';

  return (
    <AnimatePresence>
      <motion.div key="pacing-banner"
        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className={`border rounded-2xl p-4 space-y-3 print:hidden ${cfg.bg} ${cfg.border}`}
      >
        <div className={`flex items-start gap-2.5 ${cfg.text}`}>
          <span className="flex-shrink-0 mt-0.5">{cfg.icon}</span>
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${cfg.badgeBg}`}>
                {cfg.label}
              </span>
              <span className="text-xs leading-snug opacity-90">{result.summary}</span>
            </div>
            {!isRealistic && result.recommendations.length > 0 && (
              <ul className="space-y-0.5 pt-0.5">
                {result.recommendations.map((rec, i) => (
                  <li key={i} className={`text-xs opacity-75 flex items-start gap-1.5 ${cfg.text}`}>
                    <span className="flex-shrink-0 mt-1.5 w-1 h-1 rounded-full bg-current" />
                    {rec}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button onClick={onDismiss} aria-label="Dismiss pacing notice"
            className={`flex-shrink-0 p-1 rounded-lg opacity-40 hover:opacity-80 transition-opacity ${cfg.text}`}>
            <XCircle size={14} />
          </button>
        </div>

        {!isRealistic && (
          <div className="flex gap-2 flex-wrap pt-0.5">
            <button onClick={onAutoAdjust}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all hover:brightness-95 ${cfg.border} ${cfg.text} ${cfg.bg}`}>
              Adjust lesson automatically
            </button>
            {result.suggestedDuration && (
              <button onClick={() => onIncreaseDuration(result.suggestedDuration!)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all hover:brightness-95 ${cfg.border} ${cfg.text} ${cfg.bg}`}>
                Use {result.suggestedDuration} instead
              </button>
            )}
            <button onClick={onDismiss}
              className="text-xs font-medium px-3 py-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-all">
              Keep current pacing
            </button>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

// ── Adjustment summary banner (shown after "Adjust lesson automatically") ─────
function AdjustmentSummaryBanner({
  summary,
  onUndo,
  onDismiss,
}: {
  summary: AdjustmentSummary;
  onUndo: () => void;
  onDismiss: () => void;
}) {
  const changeSentence = summary.changes
    .map(c => {
      const noun = c.fromCount === 1 ? 'problem' : 'problems';
      return `${c.label} was reduced from ${c.fromCount} ${noun} to ${c.toCount}`;
    })
    .join(', and ');

  const durationSentence =
    `Estimated duration changed from ${summary.originalMinutes} minutes to ${summary.newMinutes} minutes.`;

  return (
    <AnimatePresence>
      <motion.div key="adjustment-summary"
        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="border border-blue-200 rounded-2xl p-4 bg-blue-50 space-y-3 print:hidden"
      >
        <div className="flex items-start gap-2.5 text-blue-800">
          <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                Lesson adjusted
              </span>
            </div>
            <p className="text-xs leading-snug opacity-90">
              {changeSentence}. {durationSentence}
            </p>
          </div>
          <button onClick={onDismiss} aria-label="Dismiss adjustment summary"
            className="flex-shrink-0 p-1 rounded-lg opacity-40 hover:opacity-80 transition-opacity text-blue-800">
            <XCircle size={14} />
          </button>
        </div>

        <div className="flex gap-2 flex-wrap pt-0.5">
          <button onClick={onUndo}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-blue-200 text-blue-800 bg-blue-50 transition-all hover:brightness-95 flex items-center gap-1.5">
            <RotateCcw size={12} />
            Undo adjustment
          </button>
          <button onClick={onDismiss}
            className="text-xs font-medium px-3 py-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-all">
            Keep adjustment
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Theme context (threads theme into SectionCards without prop-drilling) ────
const LessonThemeContext = React.createContext<{ accentHex: string; surfaceBg: string } | null>(null);

// ── Section display / edit card ───────────────────────────────────────────────
const SECTION_COLORS: Record<string, string> = {
  success:  'border-l-green-400  bg-green-50/50',
  mat:      'border-l-slate-400  bg-slate-50/50',
  vocab:    'border-l-indigo-400 bg-indigo-50/40',
  hook:     'border-l-blue-400   bg-blue-50/40',
  direct:   'border-l-violet-400 bg-violet-50/40',
  guided:   'border-l-emerald-400 bg-emerald-50/40',
  indep:    'border-l-amber-400  bg-amber-50/40',
  formative:'border-l-orange-400 bg-orange-50/40',
  exit:     'border-l-rose-400   bg-rose-50/40',
  diff:     'border-l-cyan-400   bg-cyan-50/40',
  ext:      'border-l-purple-400 bg-purple-50/40',
  hw:       'border-l-teal-400   bg-teal-50/40',
  reflect:  'border-l-pink-400   bg-pink-50/40',
};

function SectionCard({
  colorKey, heading, children, timeMin, estimatedMin, pacingAssessment, sectionKey, isHidden, onToggleHidden,
}: {
  colorKey: string;
  heading: string;
  children: React.ReactNode;
  /** Engine-allocated time in minutes (guaranteed to sum to lesson duration). */
  timeMin?: number;
  /** AI-estimated actual time from pacing analysis (may differ when content is dense). */
  estimatedMin?: number;
  /** Overall pacing verdict — used to decide whether to show a section-level warning. */
  pacingAssessment?: 'realistic' | 'tight' | 'overloaded';
  /** Lesson field key used for section visibility tracking. */
  sectionKey?: string;
  isHidden?: boolean;
  onToggleHidden?: () => void;
}) {
  const themeCtx = React.useContext(LessonThemeContext);
  // Warn if AI estimates this section will run >15% over its allocated time
  // and the overall lesson is not already rated realistic.
  const overPct = (timeMin && estimatedMin) ? (estimatedMin - timeMin) / timeMin : 0;
  const sectionWarn = overPct > 0.15 && !!pacingAssessment && pacingAssessment !== 'realistic';

  const colorCls   = themeCtx ? '' : (SECTION_COLORS[colorKey] ?? 'border-l-slate-300 bg-slate-50/40');
  const colorStyle = themeCtx
    ? { borderLeftColor: themeCtx.accentHex, backgroundColor: themeCtx.surfaceBg }
    : {};

  return (
    <div className={`border-l-4 rounded-r-2xl rounded-bl-2xl p-5 ${colorCls} ${isHidden ? 'opacity-40 print:hidden' : ''}`} style={colorStyle}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="font-bold text-slate-800 text-sm leading-snug flex-1">
          {heading}
          {isHidden && <span className="ml-2 text-[10px] font-medium text-slate-400 bg-slate-100 rounded-full px-2 py-0.5 align-middle">Hidden</span>}
        </h3>
        <div className="flex items-center gap-1.5 flex-shrink-0 print:hidden">
          {onToggleHidden && (
            <button onClick={onToggleHidden} title={isHidden ? 'Show section' : 'Hide section'}
              className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100/80 transition-colors">
              {isHidden ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          )}
          {timeMin !== undefined && (
            <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
              sectionWarn ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
            }`}>
              <Clock size={10} />
              {timeMin} min
            </span>
          )}
          {sectionWarn && estimatedMin && (
            <span className="text-[11px] text-amber-600 font-medium whitespace-nowrap" title="AI-estimated actual time based on content density">
              ≈{estimatedMin} actual
            </span>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function BulletList({ text, numbered }: { text: string; numbered?: boolean }) {
  const items = text.split('\n').filter(l => l.trim());
  return (
    <ul className="space-y-1.5">
      {items.map((item, i) => {
        const clean = item.replace(/^[•\-\d.]+\s*/, '');
        return (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
            {numbered
              ? <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
              : <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-400 flex-shrink-0" />
            }
            {clean}
          </li>
        );
      })}
    </ul>
  );
}

function EditableField({
  label, value, onChange, rows = 4,
}: { label: string; value: string; onChange: (v: string) => void; rows?: number }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{label}</label>
      <Textarea value={value} onChange={e => onChange(e.target.value)} rows={rows}
        className="bg-white rounded-xl border-slate-200 text-sm resize-none focus-visible:ring-primary/20 focus-visible:border-primary" />
    </div>
  );
}

// ── Validation report card (developer-facing alignment check) ─────────────────
function ValidationReport({ results }: { results: ValidationSectionResult[] }) {
  const [open, setOpen] = React.useState(false);
  const failures = results.filter(r => r.status === 'FAIL');
  const allPass = failures.length === 0;

  return (
    <div className={`rounded-2xl border shadow-sm p-4 text-xs font-mono ${allPass ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-300'}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <span className={`font-bold uppercase tracking-wider ${allPass ? 'text-green-700' : 'text-red-700'}`}>
          {allPass ? '✓ Validator: All sections PASS' : `✗ Validator: ${failures.length} section${failures.length > 1 ? 's' : ''} FAIL`}
        </span>
        <span className="text-slate-400">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-1.5 border-t pt-3 border-current/20">
          <p className="text-slate-500 mb-2">Required concept: <span className="font-bold text-slate-700">{results[0]?.requiredConcept ?? '—'}</span></p>
          {results.map(r => (
            <div key={r.section} className={`flex gap-2 items-start ${r.status === 'FAIL' ? 'text-red-700' : 'text-green-700'}`}>
              <span className="shrink-0 w-5">{r.status === 'FAIL' ? '✗' : '✓'}</span>
              <span className="font-semibold w-40 shrink-0">{r.section}</span>
              <span className="text-slate-600 break-words">{r.notes}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface LessonPlannerProps {
  onGoHome: () => void;
  onGoMyResources: () => void;
  onNavigateTo: (section: string, context?: { fromLessonTitle?: string; fromLessonId?: string }) => void;
  /** Called after the lesson is saved — opens the Lesson Workspace for this lesson. */
  onOpenWorkspace?: (lessonId: string) => void;
  initialResource?: SavedResource | null;
  userId?: string;
  initialMode?: 'ai' | 'curriculum';
  /** When set, CurriculumFlow immediately pre-loads this curriculum from the Hub */
  initialCurriculumId?: number;
}

type PlannerView = 'form' | 'loading' | 'result';

// ── Main component ────────────────────────────────────────────────────────────
export function LessonPlanner({ onGoHome, onGoMyResources, onNavigateTo, onOpenWorkspace, initialResource, userId = '', initialMode = 'ai', initialCurriculumId }: LessonPlannerProps) {
  const { saveResource, updateResource, resources } = useResources();
  const { toasts, show: showToast } = useToasts();
  const { profile } = useProfile();
  const { hasAccess } = useBilling();

  const [view, setView] = useState<PlannerView>('form');
  const [lessonMode, setLessonMode] = useState<'ai' | 'curriculum'>(initialMode);
  const [isEditing, setIsEditing] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [inputs, setInputs] = useState<LessonInputs>({
    skillToTeach: '', grade: '', subject: '', standard: '',
    duration: '', instructions: '',
    requiredStrategy: '',
    learningObjective: '',
    tieredPractice: false,
    multimediaMode: 'none',
  });
  const [errors, setErrors] = useState<LessonErrors>({});

  const [lesson, setLesson] = useState<GeneratedLesson | null>(null);
  const [editableLesson, setEditableLesson] = useState<GeneratedLesson | null>(null);
  const { savedDefault: lessonDefaultTheme, saveDefault: saveLessonDefault, restoreDefault: restoreLessonDefault } = useDefaultThemes('lesson');
  const [lessonThemeId, setLessonThemeId] = useState<TeachBudThemeId>(lessonDefaultTheme);

  // Apply profile defaults once when profile loads (new lesson only)
  const profileDefaultsApplied = useRef(false);
  useEffect(() => {
    if (!profile || initialResource || profileDefaultsApplied.current) return;
    profileDefaultsApplied.current = true;
    setInputs(prev => ({
      ...prev,
      grade:    prev.grade    || (profile.grades.length === 1 ? profile.grades[0] : ''),
      subject:  prev.subject  || (profile.subjects.length === 1 ? profile.subjects[0] : ''),
      duration: prev.duration || (profile.defaultDuration ? `${profile.defaultDuration} minutes` : ''),
    }));
    if (profile.defaultLessonTheme) setLessonThemeId(profile.defaultLessonTheme as TeachBudThemeId);
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const [branding, setBranding] = useState<BrandingData>(defaultBranding());
  const [showBrandingPanel, setShowBrandingPanel] = useState(false);
  const [lessonHiddenSections, setLessonHiddenSections] = useState<string[]>([]);
  const [blueprintDirty, setBlueprintDirty] = useState(false);
  const preEditLessonRef = useRef<GeneratedLesson | null>(null);
  // Used to detect cancelled generations — prevents stale results from landing
  const generationIdRef = useRef(0);

  const [savedResourceId, setSavedResourceId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmNewOpen, setConfirmNewOpen] = useState(false);

  // Standards alignment check — debounced, advisory-only
  const [alignment, setAlignment] = useState<AlignmentState>({ status: 'idle' });
  const alignmentTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Post-generation pacing check — advisory-only
  const [pacing, setPacing] = useState<PacingState>({ status: 'idle' });
  const [pacingDismissed, setPacingDismissed] = useState(false);

  // Auto-adjust summary + undo snapshot
  const [adjustmentSummary, setAdjustmentSummary] = useState<AdjustmentSummary | null>(null);
  const [preAdjustSnapshot, setPreAdjustSnapshot] = useState<{
    lesson: GeneratedLesson;
    pacing: PacingState;
  } | null>(null);

  // Standards Picker
  const [pickedStandard, setPickedStandard] = useState<Standard | null>(null);
  const [showStandardsPicker, setShowStandardsPicker] = useState(false);
  // Source type for manually-entered standards (only relevant when pickedStandard is null)
  const [manualStandardSource, setManualStandardSource] = useState<'manual' | 'district'>('manual');

  // Completeness & blueprint integrity — runs on every lesson change (generation or reopen)
  const [completenessIssues, setCompletenessIssues] = useState<string[]>([]);

  // Tracks whether the current lesson was loaded from a saved resource
  const [isReopened, setIsReopened] = useState(false);

  // Debounced standards-alignment check
  // Fires when skill + standard both have meaningful content; resets on clear.
  useEffect(() => {
    const skill    = inputs.skillToTeach.trim();
    const standard = inputs.standard.trim();

    if (skill.length < 15 || standard.length < 4) {
      setAlignment({ status: 'idle' });
      if (alignmentTimer.current) clearTimeout(alignmentTimer.current);
      return;
    }

    if (alignmentTimer.current) clearTimeout(alignmentTimer.current);
    setAlignment({ status: 'checking' });

    alignmentTimer.current = setTimeout(async () => {
      try {
        const result = await checkAlignmentAI(skill, standard, inputs.grade, inputs.subject);
        setAlignment({ status: 'done', result });
      } catch {
        setAlignment({ status: 'error' });
      }
    }, 900);

    return () => { if (alignmentTimer.current) clearTimeout(alignmentTimer.current); };
  }, [inputs.skillToTeach, inputs.standard, inputs.grade, inputs.subject]);

  // Post-generation pacing check — runs once per generated lesson, advisory-only
  useEffect(() => {
    if (!lesson || view !== 'result') return;
    setPacing({ status: 'checking' });
    setPacingDismissed(false);
    const sections = buildPacingSummary(lesson);
    checkPacingAI({
      duration: lesson.duration,
      grade: inputs.grade,
      subject: inputs.subject,
      tieredPractice: !!lesson.tieredPractice,
      sections,
    }).then(result => {
      setPacing({ status: 'done', result });
    }).catch(() => {
      setPacing({ status: 'error' }); // silent — pacing check is advisory
    });
  // lesson identity changes when a new lesson is generated; inputs.grade/subject
  // are stable for the lifetime of a generated lesson.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson]);

  // Completeness & blueprint integrity check — runs whenever a lesson is set
  useEffect(() => {
    if (!lesson) { setCompletenessIssues([]); return; }
    const required: [string, string | undefined][] = [
      ['Warm-Up',              lesson.warmUp],
      ['Direct Instruction',   lesson.directInstruction],
      ['Guided Practice',      lesson.guidedPractice],
      ['Independent Practice', lesson.independentPractice],
      ['Exit Ticket',          lesson.exitTicket],
    ];
    const missing = required
      .filter(([, v]) => !v || v.trim().length < 20)
      .map(([name]) => name);
    setCompletenessIssues(missing);
  }, [lesson]);

  // Open from saved resource
  useEffect(() => {
    if (!initialResource) return;
    try {
      if (initialResource.lessonData) {
        const parsed = JSON.parse(initialResource.lessonData) as GeneratedLesson;
        setLesson(parsed);
        setEditableLesson({ ...parsed });
        setSavedResourceId(initialResource.id);
        setIsReopened(true);
        // Restore inputs so alignment + pacing effects fire with correct values
        setInputs({
          skillToTeach: parsed.skillToTeach
            || parsed.objective.replace(/^Students will be able to /i, ''),
          grade: initialResource.grade,
          subject: initialResource.subject,
          standard: initialResource.standard ?? '',
          duration: parsed.duration,
          instructions: '',
        });
        setView('result');
      }
    } catch {
      // Couldn't parse — just show form
    }
  }, [initialResource]);

  function setField<K extends keyof LessonInputs>(key: K, val: LessonInputs[K]) {
    setInputs((prev) => ({ ...prev, [key]: val }));
    if (errors[key as keyof LessonErrors]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function validate(): LessonErrors {
    const e: LessonErrors = {};
    if (!inputs.skillToTeach.trim()) e.skillToTeach = 'Skill to Teach is required. Describe the exact academic skill students will learn.';
    if (!inputs.grade)               e.grade    = 'Please select a grade level.';
    if (!inputs.subject)             e.subject  = 'Please select a subject.';
    if (!inputs.duration)            e.duration = 'Please select a duration.';
    return e;
  }

  function handleCancelGeneration() {
    generationIdRef.current++;
    setView('form');
  }

  async function handleGenerate(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // ── Subscription gate ─────────────────────────────────────────────────────
    // Check access proactively so the teacher never waits for an API round-trip
    // only to get a 402. The API enforces this too — this is a UX shortcut.
    if (!hasAccess) {
      showToast('Start your free 7-day trial to generate resources. Click Upgrade in the menu.', 'error');
      return;
    }

    const myGenId = ++generationIdRef.current;
    setView('loading');

    let generated: GeneratedLesson;
    try {
      // AI generation: the lesson engine calls the API with the teacher's exact
      // Skill to Teach and receives content anchored 100% to that skill.
      // Works for any skill, any grade, any subject, any standard — no routing,
      // no hardcoded templates, no content drift.
      const _t0 = Date.now();
      const aiContent = await generateLessonAI(inputs, userId);
      if (generationIdRef.current !== myGenId) return; // teacher cancelled
      generated = assembleAiLesson(inputs, aiContent as Record<string, unknown>);
      if (generated.trace) generated.trace.generationTimeMs = Date.now() - _t0;
    } catch (err) {
      if (generationIdRef.current !== myGenId) return; // teacher cancelled
      if (err instanceof SubscriptionRequiredError) {
        // API returned 402 — billing status may not have refreshed yet. Show
        // the human-readable message from the server (e.g. "Start your free
        // 7-day trial") rather than a confusing connection error.
        setView('form');
        showToast(err.message, 'error');
        return;
      }
      // AI generation failed — do NOT silently fall back to the local generator.
      // The local generator cannot produce strategy-specific content and will
      // generate incorrect lessons. Show an error and let the teacher retry.
      console.error('[TeachBud] AI generation failed:', err);
      setView('form');
      showToast('Generation failed — please check your connection and try again.', 'error');
      return;
    }

    // Attach standard source metadata before storing
    if (pickedStandard) {
      generated.standardSource    = 'picked';
      generated.standardFramework = pickedStandard.framework;
      generated.standardVersion   = pickedStandard.version;
      generated.standardCode      = pickedStandard.code;
    } else if (inputs.standard.trim()) {
      generated.standardSource = manualStandardSource;
    }

    setLesson(generated);
    setEditableLesson({ ...generated });
    // Auto-save immediately — the lesson record exists before the teacher clicks anything.
    // Companion resources (Activity Sheet, Presentation, Assessment) link to this ID.
    const autoId = saveResource({
      type: 'lesson',
      title:     generated.title,
      grade:     generated.grade,
      subject:   generated.subject,
      topic:     generated.topic,
      standard:  generated.standard,
      plainText: lessonToText(generated),
      lessonData: JSON.stringify(generated),
    });
    setSavedResourceId(autoId);
    setCopied(false);
    setIsEditing(false);
    setAdjustmentSummary(null);
    setPreAdjustSnapshot(null);
    setView('result');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    showToast('Lesson generated and saved!');
  }

  function handleCopy() {
    const l = isEditing ? editableLesson : lesson;
    if (!l) return;
    navigator.clipboard.writeText(lessonToText(l)).then(() => {
      setCopied(true);
      showToast('Lesson copied to clipboard!');
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => showToast('Failed to copy — please try again.', 'error'));
  }

  function handleSave() {
    const l = isEditing ? editableLesson : lesson;
    if (!l) return;
    const text = lessonToText(l);
    const resourceData = {
      type: 'lesson' as const,
      title: l.title,
      grade: l.grade,
      subject: l.subject,
      topic: l.topic,
      standard: l.standard,
      plainText: text,
      lessonData: JSON.stringify(l),
    };

    if (savedResourceId) {
      updateResource(savedResourceId, resourceData);
      showToast('Lesson updated in My Resources!');
    } else {
      const id = saveResource(resourceData);
      setSavedResourceId(id);
      showToast('Lesson saved to My Resources!');
    }
  }

  function handleDoneEditing() {
    if (!editableLesson) return;
    // Detect whether blueprint-linked instructional content changed.
    // Use FULL content comparison (not the 120-char sync key) so that edits
    // at any position — including appending to long sections — trigger the banner.
    if (lesson) {
      const fullContent = (l: GeneratedLesson) =>
        [l.warmUp, l.guidedPractice, l.independentPractice, l.exitTicket].join('');
      if (fullContent(lesson) !== fullContent(editableLesson)) setBlueprintDirty(true);
    }
    setLesson({ ...editableLesson });
    setIsEditing(false);
    // Auto-persist edits so the saved record always matches what the teacher sees
    if (savedResourceId) {
      updateResource(savedResourceId, {
        title:     editableLesson.title,
        grade:     editableLesson.grade,
        subject:   editableLesson.subject,
        standard:  editableLesson.standard,
        plainText: lessonToText(editableLesson),
        lessonData: JSON.stringify(editableLesson),
      });
    }
  }

  function handlePrint() {
    window.print();
  }

  function handleStartNew() {
    // Lessons are auto-saved on generation, so there is no unsaved-lesson case.
    doStartNew();
  }

  function doStartNew() {
    setConfirmNewOpen(false);
    setView('form');
    setLessonMode('ai');
    setLesson(null);
    setEditableLesson(null);
    setSavedResourceId(null);
    setCopied(false);
    setIsEditing(false);
    setSubmitted(false);
    setInputs({ skillToTeach: '', grade: '', subject: '', standard: '', duration: '', instructions: '' });
    setErrors({});
    setPacing({ status: 'idle' });
    setPacingDismissed(false);
    setAdjustmentSummary(null);
    setPreAdjustSnapshot(null);
    setPickedStandard(null);
    setShowStandardsPicker(false);
    setManualStandardSource('manual');
    setCompletenessIssues([]);
    setIsReopened(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ── Pacing action handlers ────────────────────────────────────────────────

  function handleDismissPacing() {
    setPacingDismissed(true);
  }

  /** Go back to the form with the suggested longer duration pre-filled. */
  function handleIncreaseDuration(suggestedDuration: string) {
    setInputs(prev => ({ ...prev, duration: suggestedDuration }));
    setPacingDismissed(true);
    setView('form');
    showToast(`Duration set to ${suggestedDuration}. Review and regenerate your lesson.`);
  }

  /**
   * Trim practice sections client-side to fit the requested duration.
   * Removes the last N numbered items from independent practice (then guided
   * practice if still over budget), keeping a minimum of 2 items per section.
   * Saves a snapshot for Undo and shows a persistent AdjustmentSummaryBanner.
   */
  function handleAutoAdjust() {
    const l = isEditing ? editableLesson : lesson;
    if (!l || pacing.status !== 'done') return;

    const { breakdown, requestedMinutes } = pacing.result;
    const totalEstimated = Math.round(Object.values(breakdown).reduce((a, b) => a + b, 0));
    let remainingExcess = totalEstimated - requestedMinutes;

    if (remainingExcess <= 0) { setPacingDismissed(true); return; }

    const lessonChanges: Partial<GeneratedLesson> = {};
    const adjustChanges: AdjustmentChange[] = [];
    let minutesSaved = 0;

    function trimSection(key: 'independentPractice' | 'guidedPractice', label: string) {
      const text = (lessonChanges[key] ?? l![key]) as string | undefined;
      if (!text || !breakdown[key] || remainingExcess <= 0) return;
      const items = splitNumberedItems(text);
      if (items.length <= 2) return;
      const minPerItem = breakdown[key] / items.length;
      if (minPerItem <= 0) return;
      const remove = Math.min(Math.ceil(remainingExcess / minPerItem), items.length - 2);
      if (remove <= 0) return;
      lessonChanges[key] = items.slice(0, items.length - remove).join('\n') as GeneratedLesson[typeof key];
      adjustChanges.push({ label, fromCount: items.length, toCount: items.length - remove });
      const saved = remove * minPerItem;
      minutesSaved += saved;
      remainingExcess -= saved;
    }

    trimSection('independentPractice', 'Independent Practice');
    trimSection('guidedPractice', 'Guided Practice');

    if (adjustChanges.length === 0) {
      showToast('Lesson is at minimum — no further trimming possible.', 'error');
      setPacingDismissed(true);
      return;
    }

    // Save snapshot for Undo
    setPreAdjustSnapshot({ lesson: l, pacing });

    const updated = { ...l, ...lessonChanges };
    setLesson(updated);
    setEditableLesson({ ...updated });
    setPacingDismissed(true);
    setAdjustmentSummary({
      changes: adjustChanges,
      originalMinutes: totalEstimated,
      newMinutes: Math.round(totalEstimated - minutesSaved),
    });
  }

  /** Undo the last auto-adjustment, restoring lesson content and pacing state. */
  function handleUndoAdjust() {
    if (!preAdjustSnapshot) return;
    setLesson(preAdjustSnapshot.lesson);
    setEditableLesson({ ...preAdjustSnapshot.lesson });
    setPacing(preAdjustSnapshot.pacing);
    setPacingDismissed(false);
    setAdjustmentSummary(null);
    setPreAdjustSnapshot(null);
  }

  /** Dismiss the adjustment summary without undoing. */
  function handleDismissAdjustment() {
    setAdjustmentSummary(null);
    setPreAdjustSnapshot(null);
  }

  // ── Standards picker handlers ─────────────────────────────────────────────

  function handleStandardSelect(standard: Standard) {
    setPickedStandard(standard);
    setShowStandardsPicker(false);
    setField('standard', formatStandardForField(standard));
  }

  function handleClearStandard() {
    setPickedStandard(null);
    setShowStandardsPicker(false);
    setField('standard', '');
    setManualStandardSource('manual'); // reset to neutral when clearing a picked standard
  }

  function handleManualStandard() {
    setShowStandardsPicker(false);
    // Keep any existing text the teacher typed; just close the picker
  }

  function handleCreateFrom(section: string, sectionLabel: string) {
    const l = isEditing ? editableLesson : lesson;
    if (!l) return;
    // Save first if not already saved
    let resId = savedResourceId;
    if (!resId) {
      const text = lessonToText(l);
      resId = saveResource({
        type: 'lesson',
        title: l.title,
        grade: l.grade,
        subject: l.subject,
        topic: l.topic,
        standard: l.standard,
        plainText: text,
        lessonData: JSON.stringify(l),
      });
      setSavedResourceId(resId);
      showToast(`Lesson saved. Opening ${sectionLabel}…`);
    }
    setTimeout(() => {
      onNavigateTo(section, { fromLessonTitle: l.title, fromLessonId: resId ?? undefined });
    }, 600);
  }

  // ── Style helpers ────────────────────────────────────────────────────────
  const labelCls = 'text-sm font-semibold text-slate-700';
  const inputCls = (field: keyof LessonErrors) =>
    `h-11 bg-white rounded-xl border transition-all focus-visible:ring-primary/20 focus-visible:border-primary ${
      submitted && errors[field] ? 'border-red-400' : 'border-slate-200'
    }`;

  // ── Loading view ────────────────────────────────────────────────────────
  if (view === 'loading') {
    return <GenerationProgress resourceType="lesson" onCancel={handleCancelGeneration} />;
  }

  // ── Form view ───────────────────────────────────────────────────────────
  if (view === 'form') {
    // Curriculum-based mode — show the full CurriculumFlow wizard instead of the form
    if (lessonMode === 'curriculum') {
      return (
        <>
          <ToastContainer toasts={toasts} />
          <CurriculumFlow
            userId={userId}
            initialCurriculumId={initialCurriculumId}
            onBack={() => setLessonMode('ai')}
            onComplete={async (curriculumInputs, source) => {
              const inputsWithSource = { ...curriculumInputs, curriculumSource: source };
              setInputs(inputsWithSource);
              setView('loading');
              let generated: GeneratedLesson;
              try {
                const _t0 = Date.now();
                const aiContent = await generateLessonAI(inputsWithSource, userId);
                generated = assembleAiLesson(inputsWithSource, aiContent as Record<string, unknown>);
                if (generated.trace) generated.trace.generationTimeMs = Date.now() - _t0;
              } catch (err) {
                // AI generation failed — do NOT fall back to the local generator.
                console.error('[TeachBud] AI generation failed (curriculum flow):', err);
                const msg = err instanceof Error ? err.message : 'Unknown error';
                setView('form');
                setLessonMode('ai');
                showToast(`Lesson generation failed — please try again. (${msg})`, 'error');
                return;
              }
              setLesson(generated);
              setEditableLesson({ ...generated });
              // Auto-save on curriculum generation too
              const autoId = saveResource({
                type: 'lesson',
                title:     generated.title,
                grade:     generated.grade,
                subject:   generated.subject,
                topic:     generated.topic,
                standard:  generated.standard,
                plainText: lessonToText(generated),
                lessonData: JSON.stringify(generated),
              });
              setSavedResourceId(autoId);
              setView('result');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </>
      );
    }

    return (
      <>
        <ToastContainer toasts={toasts} />
        <motion.div key="form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl mx-auto space-y-6 print:hidden">

          {/* Header */}
          <div className="flex items-center gap-3">
            <button onClick={onGoHome}
              className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all">
              <ChevronLeft size={18} />
            </button>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
                <BookOpen size={18} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900 leading-tight">Lesson Planner</h2>
                <p className="text-xs text-slate-500">Choose how you want to build your lesson.</p>
              </div>
            </div>
          </div>

          {/* Mode selector */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setLessonMode('ai')}
              className={`flex items-center gap-3 p-4 rounded-2xl border-2 text-left transition-all ${
                lessonMode === 'ai' ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
                lessonMode === 'ai' ? 'bg-primary text-white' : 'bg-slate-100 text-slate-500'
              }`}>
                <Sparkles size={18} />
              </div>
              <div>
                <p className={`text-sm font-bold ${lessonMode === 'ai' ? 'text-primary' : 'text-slate-700'}`}>
                  AI-Generated Lesson
                </p>
                <p className="text-xs text-slate-400 mt-0.5">Describe what you want to teach</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setLessonMode('curriculum')}
              className="flex items-center gap-3 p-4 rounded-2xl border-2 border-slate-200 hover:border-teal-400 bg-white text-left transition-all"
            >
              <div className="w-9 h-9 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                <BookMarked size={18} className="text-teal-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-700">Curriculum-Based Lesson</p>
                <p className="text-xs text-slate-400 mt-0.5">Upload your curriculum first</p>
              </div>
            </button>
          </div>

          {/* Form card */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 md:p-8">
            <form onSubmit={handleGenerate} noValidate className="space-y-6">

              {/* Skill to Teach */}
              <div className="space-y-2">
                <Label className={labelCls}>Skill to Teach <span className="text-red-400">*</span></Label>
                <p className="text-xs text-slate-400">
                  Describe the <strong className="text-slate-600">exact academic skill</strong> students will learn — not
                  the subject or unit topic. The lesson title, objective, vocabulary, examples, and assessments are all
                  derived from this field.
                </p>
                <Textarea
                  placeholder="e.g. Multiply a fraction by a whole number using visual models, repeated addition, and equations."
                  value={inputs.skillToTeach}
                  onChange={(e) => setField('skillToTeach', e.target.value)}
                  rows={3}
                  className={`bg-white rounded-xl border transition-all focus-visible:ring-primary/20 focus-visible:border-primary resize-none ${
                    submitted && errors.skillToTeach ? 'border-red-400' : 'border-slate-200'}`}
                />
                <FieldErr msg={submitted ? errors.skillToTeach : undefined} />
              </div>

              {/* Grade + Subject */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={labelCls}>Grade Level <span className="text-red-400">*</span></Label>
                  <Select value={inputs.grade} onValueChange={(v) => setField('grade', v)}>
                    <SelectTrigger className={`${inputCls('grade')} w-full`}>
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="K">Kindergarten</SelectItem>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>Grade {i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldErr msg={submitted ? errors.grade : undefined} />
                </div>

                <div className="space-y-2">
                  <Label className={labelCls}>Subject <span className="text-red-400">*</span></Label>
                  <Select value={inputs.subject} onValueChange={(v) => setField('subject', v)}>
                    <SelectTrigger className={`${inputCls('subject')} w-full`}>
                      <SelectValue placeholder="Select subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {['English Language Arts', 'Math', 'Science', 'Social Studies', 'Other'].map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldErr msg={submitted ? errors.subject : undefined} />
                </div>
              </div>

              {/* Academic Standard + alignment check */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className={labelCls}>
                    Academic Standard <span className="text-slate-400 font-normal">(optional)</span>
                  </Label>
                  {!showStandardsPicker && !pickedStandard && (
                    <button
                      type="button"
                      onClick={() => setShowStandardsPicker(true)}
                      className="text-xs text-primary font-medium hover:underline flex-shrink-0"
                    >
                      Browse standards
                    </button>
                  )}
                  {pickedStandard && !showStandardsPicker && (
                    <button
                      type="button"
                      onClick={() => setShowStandardsPicker(true)}
                      className="text-xs text-primary font-medium hover:underline flex-shrink-0"
                    >
                      Change
                    </button>
                  )}
                </div>

                {/* Standards picker panel */}
                {showStandardsPicker && (
                  <StandardsPicker
                    onSelect={handleStandardSelect}
                    onManual={handleManualStandard}
                  />
                )}

                {/* Picked standard badge */}
                {pickedStandard && !showStandardsPicker && (
                  <PickedStandardBadge
                    standard={pickedStandard}
                    onClear={handleClearStandard}
                  />
                )}

                {/* Free-text input — shown when no standard is picked and picker is closed */}
                {!pickedStandard && !showStandardsPicker && (
                  <>
                    <Input placeholder="e.g. CCSS.MATH.CONTENT.4.NF.A.2 or type a custom standard"
                      value={inputs.standard}
                      onChange={(e) => setField('standard', e.target.value)}
                      className="h-11 bg-white rounded-xl border border-slate-200 transition-all focus-visible:ring-primary/20 focus-visible:border-primary" />
                    {/* Source type selector — let teachers identify what kind of standard they're entering */}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="text-xs text-slate-400 flex-shrink-0">Standard type:</span>
                      <button type="button" onClick={() => setManualStandardSource('manual')}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                          manualStandardSource === 'manual'
                            ? 'bg-slate-700 text-white border-slate-700'
                            : 'border-slate-200 text-slate-500 hover:border-slate-400 hover:text-slate-700'
                        }`}>
                        Teacher-Entered
                      </button>
                      <button type="button" onClick={() => setManualStandardSource('district')}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                          manualStandardSource === 'district'
                            ? 'bg-violet-600 text-white border-violet-600'
                            : 'border-slate-200 text-slate-500 hover:border-violet-300 hover:text-violet-700'
                        }`}>
                        District Standard
                      </button>
                    </div>
                  </>
                )}

                <AlignmentBanner state={alignment} />
              </div>

              {/* Required Strategy + Learning Objective */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={labelCls}>
                    Required Strategy <span className="text-slate-400 font-normal">(optional)</span>
                  </Label>
                  <p className="text-xs text-slate-400">
                    The specific method or model students must use. If included in your skill statement (e.g. "using partial quotients"), TeachBud extracts it automatically.
                  </p>
                  <Input
                    placeholder="e.g. partial quotients, area model, RACE strategy"
                    value={inputs.requiredStrategy ?? ''}
                    onChange={(e) => setField('requiredStrategy', e.target.value)}
                    className="h-11 bg-white rounded-xl border border-slate-200 transition-all focus-visible:ring-primary/20 focus-visible:border-primary"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={labelCls}>
                    Learning Objective <span className="text-slate-400 font-normal">(optional)</span>
                  </Label>
                  <p className="text-xs text-slate-400">
                    Custom SWBAT statement. Defaults to "Students will be able to [Skill to Teach]." if left blank.
                  </p>
                  <Input
                    placeholder="e.g. Students will be able to divide 4-digit numbers using partial quotients."
                    value={inputs.learningObjective ?? ''}
                    onChange={(e) => setField('learningObjective', e.target.value)}
                    className="h-11 bg-white rounded-xl border border-slate-200 transition-all focus-visible:ring-primary/20 focus-visible:border-primary"
                  />
                </div>
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label className={labelCls}>Lesson Duration <span className="text-red-400">*</span></Label>
                <Select value={inputs.duration} onValueChange={(v) => setField('duration', v)}>
                  <SelectTrigger className={`${inputCls('duration')} w-full sm:w-56`}>
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {['30 minutes', '45 minutes', '60 minutes', '90 minutes'].map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldErr msg={submitted ? errors.duration : undefined} />
                <p className="text-xs text-slate-500 leading-relaxed">
                  How long is your class period? This shapes activity timing and pacing throughout the lesson.
                </p>
              </div>

              {/* Extra instructions */}
              <div className="space-y-2">
                <Label className={labelCls}>
                  Additional Instructions or Preferences <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                <Textarea placeholder="e.g. Include a hands-on activity. My class has 28 students and we have 1:1 Chromebooks."
                  value={inputs.instructions} onChange={(e) => setField('instructions', e.target.value)} rows={3}
                  className="bg-white rounded-xl border border-slate-200 transition-all focus-visible:ring-primary/20 focus-visible:border-primary resize-none" />
              </div>

              {/* ── Generation Options ──────────────────────────────── */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-4">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Generation Options</p>
                  <p className="text-xs text-slate-400 mt-0.5">Customize what TeachBud generates. All options are optional and can be changed if you regenerate the lesson.</p>
                </div>

                {/* Tiered Practice toggle */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative mt-0.5 flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={!!inputs.tieredPractice}
                      onChange={e => setField('tieredPractice', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 rounded-full bg-slate-200 peer-checked:bg-violet-500 transition-colors" />
                    <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                      <Layers size={13} className="text-violet-500" />
                      Tiered Independent Practice
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">Generates Below-Level, On-Level, and Above-Level versions — same objective, varying scaffolding and cognitive demand.</p>
                  </div>
                </label>

                {/* Multimedia mode */}
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                    <Video size={13} className="text-amber-500" />
                    Multimedia Resources
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      { value: 'none',        label: 'None',                icon: null },
                      { value: 'video',       label: 'Suggested Video',     icon: '📺' },
                      { value: 'interactive', label: 'Interactive Resource', icon: '🖥️' },
                      { value: 'both',        label: 'Video + Interactive',  icon: '✨' },
                    ] as const).map(opt => (
                      <label key={opt.value}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium cursor-pointer transition-all ${
                          inputs.multimediaMode === opt.value
                            ? 'border-amber-400 bg-amber-50 text-amber-800'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-amber-300 hover:bg-amber-50/50'
                        }`}>
                        <input type="radio" name="multimediaMode" value={opt.value}
                          checked={inputs.multimediaMode === opt.value}
                          onChange={() => setField('multimediaMode', opt.value)}
                          className="sr-only" />
                        {opt.icon && <span>{opt.icon}</span>}
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <Button type="submit"
                className="w-full h-12 rounded-xl text-base font-semibold bg-primary hover:bg-primary/90 text-white shadow-sm hover:shadow transition-all">
                Generate Lesson Plan
              </Button>
            </form>
          </div>
        </motion.div>
      </>
    );
  }

  // ── Result view ─────────────────────────────────────────────────────────
  if (!lesson || !editableLesson) return null;
  const displayLesson = isEditing ? editableLesson : lesson;
  const st = displayLesson.sectionTimes;
  // Per-section AI time estimates from Task #9 pacing check (breakdown keys match sectionTimes keys)
  const bd = pacing.status === 'done' ? (pacing.result.breakdown ?? {}) : {};
  const pa = pacing.status === 'done' ? pacing.result.assessment : undefined;

  const lessonTheme    = getTheme(lessonThemeId);
  const lessonThemeCtx = lessonThemeId === 'modern' ? null : {
    accentHex:  lessonTheme.accentColor,
    surfaceBg:  lessonTheme.cardBg,
  };

  function setEditField(key: keyof GeneratedLesson, val: string) {
    setEditableLesson(prev => prev ? { ...prev, [key]: val } : prev);
  }

  /** Toggle a lesson section in/out of the hidden list. */
  function toggleLessonSection(key: string) {
    setLessonHiddenSections(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  }

  /** Returns visibility props for a SectionCard keyed by the given lesson field key. */
  function sp(key: string) {
    return {
      sectionKey: key,
      isHidden: lessonHiddenSections.includes(key),
      onToggleHidden: () => toggleLessonSection(key),
    };
  }

  return (
    <>
      <ToastContainer toasts={toasts} />
      <ConfirmDialog
        open={confirmNewOpen}
        message="You have an unsaved lesson. Starting a new lesson will discard it."
        onConfirm={doStartNew}
        onCancel={() => setConfirmNewOpen(false)}
      />

      <LessonThemeContext.Provider value={lessonThemeCtx}>
      <motion.div key="result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-3xl mx-auto space-y-5"
        style={lessonThemeId !== 'modern' ? { backgroundColor: lessonTheme.pageBg, padding: '1rem', borderRadius: '1.25rem' } : {}}>

        {/* Top action bar */}
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          {isEditing ? (
            <>
              <button onClick={() => { setEditableLesson({ ...lesson }); setIsEditing(false); }}
                className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors px-3 py-2 rounded-xl hover:bg-slate-100">
                <XCircle size={15} /> Cancel Edit
              </button>
              <div className="flex-1" />
              <Button size="sm" onClick={handleDoneEditing}
                className="rounded-xl bg-primary hover:bg-primary/90 text-white gap-1.5">
                <CheckCircle2 size={15} /> Done Editing
              </Button>
            </>
          ) : (
            <>
              <button onClick={() => setView('form')}
                className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors px-3 py-2 rounded-xl hover:bg-slate-100">
                <ChevronLeft size={15} /> Back to Form
              </button>
              <div className="flex-1" />
              <ThemePicker
                value={lessonThemeId} onChange={setLessonThemeId} compact label="Theme"
                resourceType="lesson"
                onSaveDefault={() => saveLessonDefault(lessonThemeId)}
                onRestoreDefault={() => setLessonThemeId(restoreLessonDefault())}
              />
              <Button variant="outline" size="sm" onClick={() => setShowBrandingPanel(v => !v)}
                className={`rounded-xl gap-1.5 transition-all ${showBrandingPanel ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}>
                <Building2 size={14} /> School
              </Button>
              <Button variant="outline" size="sm" onClick={() => { preEditLessonRef.current = lesson; setIsEditing(true); }}
                className="rounded-xl border-slate-200 hover:border-slate-300 text-slate-600 gap-1.5">
                <Pencil size={14} /> Edit Lesson
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}
                className="rounded-xl border-slate-200 hover:border-slate-300 text-slate-600 gap-1.5">
                {copied ? <CheckCheck size={15} className="text-green-500" /> : <Copy size={15} />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint}
                className="rounded-xl border-slate-200 hover:border-slate-300 text-slate-600 gap-1.5">
                <Printer size={15} /> Print
              </Button>
              <SaveStatusIndicator resourceId={savedResourceId} />
            </>
          )}
        </div>

        {/* Edit mode banner */}
        {isEditing && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3 print:hidden">
            <Pencil size={16} className="text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800 font-medium">
              Edit mode — modify any section below, then click <strong>Done Editing</strong> to save your changes.
            </p>
          </div>
        )}

        {/* Blueprint-sync warning — shown when instructional content was edited */}
        {blueprintDirty && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-300 rounded-2xl p-4 print:hidden">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800">Instructional content changed</p>
                <p className="text-xs text-amber-700 mt-1">
                  Your linked Activity Sheet and Presentation may now be out of sync with this lesson.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => {
                    if (savedResourceId && lesson) {
                      // 1. Persist the updated lesson so ActivitySheet auto-regenerates
                      updateResource(savedResourceId, {
                        type: 'lesson' as const,
                        title: lesson.title,
                        grade: lesson.grade,
                        subject: lesson.subject,
                        topic: lesson.topic,
                        standard: lesson.standard,
                        plainText: lessonToText(lesson),
                        lessonData: JSON.stringify(lesson),
                      });
                      // 2. Find saved Presentations generated from the pre-edit lesson
                      //    (identified by their syncKey matching the old lessonContentKey)
                      const oldKey = preEditLessonRef.current ? lessonContentKey(preEditLessonRef.current) : null;
                      if (oldKey && preEditLessonRef.current) {
                        let patchCount = 0;
                        for (const r of resources) {
                          if (r.type !== 'presentation' || !r.lessonData) continue;
                          try {
                            const pres = JSON.parse(r.lessonData) as GeneratedPresentation;
                            if (pres.syncKey !== oldKey) continue;
                            const patched = patchPresentationSlides(pres, preEditLessonRef.current, lesson);
                            if (patched !== pres) {
                              updateResource(r.id, { lessonData: JSON.stringify(patched) });
                              patchCount++;
                            }
                          } catch { /* skip malformed */ }
                        }
                        showToast(
                          patchCount > 0
                            ? `Lesson, Activity Sheet, and ${patchCount} Presentation${patchCount > 1 ? 's' : ''} updated.`
                            : 'Lesson and Activity Sheet updated.'
                        );
                      } else {
                        showToast('Lesson and Activity Sheet updated.');
                      }
                    } else {
                      showToast('Save your lesson first to sync linked resources.', 'error');
                    }
                    setBlueprintDirty(false);
                  }}
                    className="text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors">
                    Update Linked Resources
                  </button>
                  <button onClick={() => setBlueprintDirty(false)}
                    className="text-xs font-semibold text-slate-600 hover:text-slate-800 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                    Keep Resources Separate
                  </button>
                  <button onClick={() => {
                    if (preEditLessonRef.current) {
                      setLesson(preEditLessonRef.current);
                      setEditableLesson(preEditLessonRef.current);
                    }
                    setBlueprintDirty(false);
                  }}
                    className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                    Undo Edit
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* School branding panel */}
        <BrandingPanel
          branding={branding}
          onChange={setBranding}
          open={showBrandingPanel}
          onToggle={() => setShowBrandingPanel(v => !v)}
        />

        {/* Lesson header card */}
        <div
          className="rounded-2xl p-6 text-white shadow-lg"
          style={{ background: `linear-gradient(135deg, ${lessonTheme.headerBg1} 0%, ${lessonTheme.headerBg2} 100%)`, color: lessonTheme.headerText }}>
          {isEditing ? (
            <div className="space-y-3">
              <input value={editableLesson.title} onChange={e => setEditField('title', e.target.value)}
                className="w-full bg-white/20 rounded-xl px-4 py-2 text-xl font-bold text-white placeholder-white/50 border border-white/30 outline-none focus:border-white" />
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="inline-flex items-center gap-1 bg-white/20 rounded-full px-3 py-1">
                  <GraduationCap size={12} /> {displayLesson.gradeLabel}
                </span>
                <span className="inline-flex items-center gap-1 bg-white/20 rounded-full px-3 py-1">
                  <BookMarked size={12} /> {displayLesson.subject}
                </span>
                <span className="inline-flex items-center gap-1 bg-white/20 rounded-full px-3 py-1">
                  <Clock size={12} /> {displayLesson.duration}
                </span>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <BookOpen size={20} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold mb-1 leading-tight">{displayLesson.title}</h2>
                  {displayLesson.standard && (
                    <div className="mb-2">
                      <p className="text-blue-100 text-xs font-mono">{displayLesson.standard}</p>
                      {displayLesson.standardPlainText && (
                        <p className="text-blue-200 text-xs mt-0.5 leading-snug">{displayLesson.standardPlainText}</p>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="inline-flex items-center gap-1 text-xs font-medium bg-white/20 rounded-full px-3 py-1">
                      <GraduationCap size={12} /> {displayLesson.gradeLabel}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs font-medium bg-white/20 rounded-full px-3 py-1">
                      <BookMarked size={12} /> {displayLesson.subject}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs font-medium bg-white/20 rounded-full px-3 py-1">
                      <Clock size={12} /> {displayLesson.duration}
                    </span>
                  </div>
                </div>
              </div>
              <BrandingHeaderRow branding={branding} />
            </>
          )}
        </div>

        {/* Curriculum Source banner (curriculum-based lessons only) */}
        {displayLesson.curriculumSource && (
          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <BookMarked size={15} className="text-teal-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-teal-700 uppercase tracking-wide mb-1">Curriculum Source</p>
              <p className="text-sm font-semibold text-teal-900">{displayLesson.curriculumSource.title}</p>
              <p className="text-xs text-teal-600 mt-0.5">
                {[
                  displayLesson.curriculumSource.publisher,
                  displayLesson.curriculumSource.gradeLevel,
                  displayLesson.curriculumSource.unitInfo,
                  displayLesson.curriculumSource.fileName,
                ].filter(Boolean).join(' · ')}
              </p>
              <span className="inline-flex items-center mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                Enhanced by TeachBud
              </span>
            </div>
          </div>
        )}

        {/* Lesson at a Glance */}
        {displayLesson.lessonAtAGlance && (() => {
          // Section snapshot: "Warm-Up — 5 review questions" → split into name + count
          const sectionColors = [
            'bg-amber-50 border-amber-200 text-amber-700',
            'bg-indigo-50 border-indigo-200 text-indigo-700',
            'bg-blue-50 border-blue-200 text-blue-700',
            'bg-violet-50 border-violet-200 text-violet-700',
            'bg-rose-50 border-rose-200 text-rose-700',
          ];
          return (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <p className="text-xs font-bold text-primary uppercase tracking-wider mb-3">Lesson at a Glance</p>

              {/* Section snapshot strip */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
                {displayLesson.lessonAtAGlance!.mainStudentTasks.map((t, i) => {
                  const [sectionName, countLabel] = t.includes(' — ')
                    ? t.split(' — ')
                    : [t, ''];
                  return (
                    <div key={i} className={`rounded-xl border px-3 py-2.5 ${sectionColors[i % sectionColors.length]}`}>
                      <p className="text-[11px] font-bold leading-tight">{sectionName}</p>
                      {countLabel && (
                        <p className="text-[10px] mt-0.5 opacity-75 leading-tight">{countLabel}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Teacher actions */}
              <div className="border-t border-slate-100 pt-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Teacher Actions</p>
                <ul className="space-y-1.5">
                  {displayLesson.lessonAtAGlance!.mainTeacherActions.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                      <span className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 mt-0.5 text-[10px] font-bold">{i + 1}</span>
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })()}

        {/* Objective */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <p className="text-xs font-bold text-primary uppercase tracking-wider mb-2">Learning Objective</p>
          {isEditing
            ? <Textarea value={editableLesson.objective} onChange={e => setEditField('objective', e.target.value)}
                rows={2} className="bg-slate-50 rounded-xl border-slate-200 text-sm resize-none focus-visible:ring-primary/20" />
            : <p className="text-slate-800 text-sm leading-relaxed">{displayLesson.objective}</p>}
        </div>

        {/* Skill to Teach & Standard details */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-3">
          <div>
            <p className="text-xs font-bold text-primary uppercase tracking-wider mb-1">Skill to Teach</p>
            <p className="text-slate-800 text-sm leading-relaxed">{displayLesson.skillToTeach}</p>
          </div>
          {displayLesson.standard && (
            <div className="pt-3 border-t border-slate-100">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Academic Standard</p>
              <p className="text-slate-800 text-sm font-mono">{displayLesson.standard}</p>
              {displayLesson.standardPlainText && (
                <p className="text-slate-600 text-xs mt-1 leading-snug">{displayLesson.standardPlainText}</p>
              )}
              {/* Provenance badge */}
              {displayLesson.standardSource === 'picked' && (
                <div className="flex items-center gap-1.5 mt-2">
                  <ShieldCheck size={12} className="text-emerald-500 flex-shrink-0" />
                  <span className="text-xs text-emerald-600 font-medium">
                    Standards library entry
                    {displayLesson.standardFramework ? ` · ${displayLesson.standardFramework}` : ''}
                    {displayLesson.standardVersion ? ` ${displayLesson.standardVersion}` : ''}
                  </span>
                </div>
              )}
              {displayLesson.standardSource === 'manual' && (
                <div className="flex items-center gap-1.5 mt-2">
                  <PenLine size={12} className="text-slate-400 flex-shrink-0" />
                  <span className="text-xs text-slate-500">Teacher-entered standard</span>
                </div>
              )}
              {displayLesson.standardSource === 'district' && (
                <div className="flex items-center gap-1.5 mt-2">
                  <Building2 size={12} className="text-violet-500 flex-shrink-0" />
                  <span className="text-xs text-violet-600 font-medium">District standard</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* AI Alignment Status */}
        {displayLesson.alignmentValidated !== undefined && (
          <div className={`rounded-2xl border px-4 py-3 flex items-start gap-3 text-sm ${
            displayLesson.alignmentValidated
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}>
            <span className="text-base mt-0.5">
              {displayLesson.alignmentValidated ? '✓' : '⟳'}
            </span>
            <div>
              <span className="font-semibold">
                {displayLesson.alignmentValidated
                  ? 'Alignment validated — every section teaches the requested skill.'
                  : `Alignment corrected — ${displayLesson.regeneratedSections?.length ?? 0} section${(displayLesson.regeneratedSections?.length ?? 0) !== 1 ? 's' : ''} were auto-regenerated to match your skill.`}
              </span>
              {!displayLesson.alignmentValidated && displayLesson.regeneratedSections && displayLesson.regeneratedSections.length > 0 && (
                <p className="text-xs mt-1 opacity-80">
                  Corrected: {displayLesson.regeneratedSections.join(', ')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Reopened-lesson revalidation notice */}
        {isReopened && (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 flex items-center gap-3 print:hidden">
            <RefreshCw size={14} className="text-slate-400 flex-shrink-0" />
            <p className="text-xs text-slate-500">
              Standards alignment and pacing have been re-checked for this saved lesson. Results appear below.
            </p>
          </div>
        )}

        {/* Blueprint completeness gate result — shown when server-side auto-fix still left gaps */}
        {displayLesson.blueprintIncomplete === true && (
          <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-4 flex items-start gap-3 print:hidden">
            <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800">
                This lesson is missing required content
              </p>
              <p className="text-xs text-red-700 mt-1">
                {displayLesson.incompleteFields && displayLesson.incompleteFields.length > 0
                  ? <>The following sections could not be fully generated: <strong>{displayLesson.incompleteFields.join(', ')}</strong>. </>
                  : null}
                Generating companion resources may produce incomplete or empty content.
                Regenerate this lesson to rebuild the missing sections.
              </p>
            </div>
          </div>
        )}

        {/* Completeness & blueprint integrity notice */}
        {completenessIssues.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-4 flex items-start gap-3 print:hidden">
            <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">
                Completeness notice — {completenessIssues.length} section{completenessIssues.length !== 1 ? 's' : ''} may need attention
              </p>
              <p className="text-xs text-amber-700 mt-1">
                {completenessIssues.join(', ')} {completenessIssues.length === 1 ? 'appears' : 'appear'} empty or very short. Switch to <strong>Edit Lesson</strong> to review and fill in any gaps.
              </p>
            </div>
          </div>
        )}

        {/* Pacing validation — post-generation advisory */}
        <PacingBanner
          state={pacing}
          dismissed={pacingDismissed}
          onDismiss={handleDismissPacing}
          onIncreaseDuration={handleIncreaseDuration}
          onAutoAdjust={handleAutoAdjust}
        />

        {/* Adjustment summary — shown after "Adjust lesson automatically" */}
        {adjustmentSummary && (
          <AdjustmentSummaryBanner
            summary={adjustmentSummary}
            onUndo={handleUndoAdjust}
            onDismiss={handleDismissAdjustment}
          />
        )}

        {/* Validation Report (developer view — never shown in print/PDF) */}
        {displayLesson.validationReport && displayLesson.validationReport.length > 0 && (
          <div className="print:hidden">
            <ValidationReport results={displayLesson.validationReport} />
          </div>
        )}

        {/* Success Criteria */}
        <SectionCard colorKey="success" heading="Success Criteria" {...sp('successCriteria')}>
          {isEditing
            ? <EditableField label="Success Criteria (one per line)" value={editableLesson.successCriteria}
                onChange={v => setEditField('successCriteria', v)} rows={4} />
            : <BulletList text={displayLesson.successCriteria} />}
        </SectionCard>

        {/* Materials — split view if requiredMaterials exists, legacy otherwise */}
        {displayLesson.requiredMaterials ? (
          <>
            <SectionCard colorKey="mat" heading="Required Materials" {...sp('requiredMaterials')}>
              <BulletList text={displayLesson.requiredMaterials} />
            </SectionCard>
            {displayLesson.optionalMaterials && (
              <SectionCard colorKey="mat" heading="Optional / Supplemental Materials" {...sp('optionalMaterials')}>
                <BulletList text={displayLesson.optionalMaterials} />
              </SectionCard>
            )}
          </>
        ) : (
          <SectionCard colorKey="mat" heading="Materials" {...sp('materials')}>
            {isEditing
              ? <EditableField label="Materials (one per line)" value={editableLesson.materials}
                  onChange={v => setEditField('materials', v)} rows={5} />
              : <BulletList text={displayLesson.materials} />}
          </SectionCard>
        )}

        {/* Vocabulary */}
        <SectionCard colorKey="vocab" heading="Vocabulary" {...sp('vocabulary')}>
          {isEditing
            ? <EditableField label="Vocabulary (Term — Definition, one per line)" value={editableLesson.vocabulary}
                onChange={v => setEditField('vocabulary', v)} rows={5} />
            : (
              <ul className="space-y-2">
                {displayLesson.vocabulary.split('\n').filter(l => l.trim()).map((item, i) => {
                  const [term, ...defParts] = item.split(' — ');
                  const def = defParts.join(' — ');
                  return (
                    <li key={i} className="text-sm">
                      <span className="font-semibold text-slate-800">{term.replace(/^[•\-]\s*/, '')}</span>
                      {def && <span className="text-slate-600"> — {def}</span>}
                    </li>
                  );
                })}
              </ul>
            )}
        </SectionCard>

        {/* Teacher Preparation */}
        {displayLesson.teacherPrep && (
          <SectionCard colorKey="direct" heading="Teacher Preparation (Before Class)" {...sp('teacherPrep')}>
            <ol className="space-y-2">
              {displayLesson.teacherPrep.split('\n').filter(l => l.trim()).map((line, i) => {
                const text = line.replace(/^\d+\.\s*/, '');
                return (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0 mt-0.5 text-[11px] font-bold">
                      {i + 1}
                    </span>
                    {text}
                  </li>
                );
              })}
            </ol>
          </SectionCard>
        )}

        {/* Warm-Up */}
        <SectionCard colorKey="hook" heading="Warm-Up / Anticipatory Set" {...sp('warmUp')}
          timeMin={st?.warmUp ?? t(0.155, displayLesson.duration)}
          estimatedMin={bd.warmUp}
          pacingAssessment={pa}>
          {isEditing
            ? <EditableField label="Warm-Up" value={editableLesson.warmUp}
                onChange={v => setEditField('warmUp', v)} rows={5} />
            : <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{displayLesson.warmUp}</p>}
        </SectionCard>

        {/* Direct Instruction */}
        <SectionCard colorKey="direct" heading="Direct Instruction" {...sp('directInstruction')}
          timeMin={st?.directInstruction ?? t(0.267, displayLesson.duration)}
          estimatedMin={bd.directInstruction}
          pacingAssessment={pa}>
          {isEditing
            ? <EditableField label="Direct Instruction (numbered steps, one per line)" value={editableLesson.directInstruction}
                onChange={v => setEditField('directInstruction', v)} rows={6} />
            : <BulletList text={displayLesson.directInstruction} numbered />}
        </SectionCard>

        {/* Guided Practice */}
        <SectionCard colorKey="guided" heading="Guided Practice" {...sp('guidedPractice')}
          timeMin={st?.guidedPractice ?? t(0.200, displayLesson.duration)}
          estimatedMin={bd.guidedPractice}
          pacingAssessment={pa}>
          {isEditing
            ? <EditableField label="Guided Practice" value={editableLesson.guidedPractice}
                onChange={v => setEditField('guidedPractice', v)} rows={5} />
            : <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{displayLesson.guidedPractice}</p>}
        </SectionCard>

        {/* Independent Practice — standard or tiered */}
        {displayLesson.tieredPractice ? (
          <div className="space-y-3">
            {/* Tiered header badge */}
            <div className="flex items-center gap-2 px-1">
              <Layers size={14} className="text-violet-500" />
              <span className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Tiered Independent Practice</span>
              <span className="text-xs text-slate-400">— same objective, three entry points</span>
            </div>

            {/* Below Level */}
            <SectionCard colorKey="indep" heading="📘 Below Level" {...sp('belowLevel')}
              timeMin={Math.round((st?.independentPractice ?? t(0.267, displayLesson.duration)) / 3)}
              estimatedMin={bd.tieredPractice ? Math.round(bd.tieredPractice / 3) : undefined}
              pacingAssessment={pa}>
              <div className="mb-2">
                <span className="inline-flex items-center gap-1 text-xs font-semibold bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">
                  Scaffolded entry — accessible to all learners
                </span>
              </div>
              {isEditing
                ? <EditableField label="Below-Level Practice" value={editableLesson.tieredPractice?.belowLevel ?? ''}
                    onChange={v => setEditableLesson(prev => prev ? { ...prev, tieredPractice: { ...prev.tieredPractice!, belowLevel: v } } : prev)} rows={6} />
                : <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{displayLesson.tieredPractice.belowLevel}</p>}
            </SectionCard>

            {/* On Level */}
            <SectionCard colorKey="guided" heading="📗 On Level" {...sp('onLevel')}
              timeMin={Math.round((st?.independentPractice ?? t(0.267, displayLesson.duration)) / 3)}
              estimatedMin={bd.tieredPractice ? Math.round(bd.tieredPractice / 3) : undefined}
              pacingAssessment={pa}>
              <div className="mb-2">
                <span className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                  Grade-level standard — low floor to high ceiling
                </span>
              </div>
              {isEditing
                ? <EditableField label="On-Level Practice" value={editableLesson.tieredPractice?.onLevel ?? ''}
                    onChange={v => setEditableLesson(prev => prev ? { ...prev, tieredPractice: { ...prev.tieredPractice!, onLevel: v } } : prev)} rows={6} />
                : <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{displayLesson.tieredPractice.onLevel}</p>}
            </SectionCard>

            {/* Above Level */}
            <SectionCard colorKey="ext" heading="📙 Above Level" {...sp('aboveLevel')}
              timeMin={Math.round((st?.independentPractice ?? t(0.267, displayLesson.duration)) / 3)}
              estimatedMin={bd.tieredPractice ? Math.round(bd.tieredPractice / 3) : undefined}
              pacingAssessment={pa}>
              <div className="mb-2">
                <span className="inline-flex items-center gap-1 text-xs font-semibold bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
                  Extended challenge — transfer, create, evaluate
                </span>
              </div>
              {isEditing
                ? <EditableField label="Above-Level Practice" value={editableLesson.tieredPractice?.aboveLevel ?? ''}
                    onChange={v => setEditableLesson(prev => prev ? { ...prev, tieredPractice: { ...prev.tieredPractice!, aboveLevel: v } } : prev)} rows={6} />
                : <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{displayLesson.tieredPractice.aboveLevel}</p>}
            </SectionCard>
          </div>
        ) : (
          <SectionCard colorKey="indep" heading="Independent Practice" {...sp('independentPractice')}
            timeMin={st?.independentPractice ?? t(0.267, displayLesson.duration)}
            estimatedMin={bd.independentPractice}
            pacingAssessment={pa}>
            {isEditing
              ? <EditableField label="Independent Practice" value={editableLesson.independentPractice}
                  onChange={v => setEditField('independentPractice', v)} rows={4} />
              : <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{displayLesson.independentPractice}</p>}
          </SectionCard>
        )}

        {/* Formative Assessment */}
        <SectionCard colorKey="formative" heading="Formative Assessment (During Lesson)" {...sp('formativeAssessment')}>
          {isEditing
            ? <EditableField label="Formative Assessment strategies" value={editableLesson.formativeAssessment}
                onChange={v => setEditField('formativeAssessment', v)} rows={4} />
            : <BulletList text={displayLesson.formativeAssessment} />}
        </SectionCard>

        {/* Exit Ticket */}
        <SectionCard colorKey="exit" heading="Exit Ticket" {...sp('exitTicket')}
          timeMin={st?.exitTicket ?? t(0.111, displayLesson.duration)}
          estimatedMin={bd.exitTicket}
          pacingAssessment={pa}>
          {isEditing
            ? <EditableField label="Exit Ticket prompts (one per line)" value={editableLesson.exitTicket}
                onChange={v => setEditField('exitTicket', v)} rows={4} />
            : <BulletList text={displayLesson.exitTicket} numbered />}
        </SectionCard>

        {/* Differentiation */}
        <SectionCard colorKey="diff" heading="Differentiation Resources" {...sp('differentiation')}>
          {isEditing
            ? <EditableField label="Differentiation" value={editableLesson.differentiation}
                onChange={v => setEditField('differentiation', v)} rows={7} />
            : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-bold text-cyan-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Users size={12} /> Support Strategies
                  </p>
                  <ul className="space-y-1.5">
                    {displayLesson.differentiation.split('\n')
                      .filter(l => l.startsWith('•') && !l.toLowerCase().includes('extension'))
                      .map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
                          {s.replace(/^•\s*/, '')}
                        </li>
                      ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Lightbulb size={12} /> Extension / Enrichment
                  </p>
                  <ul className="space-y-1.5">
                    {displayLesson.differentiation.split('\n')
                      .filter(l => l.startsWith('•'))
                      .slice(3)
                      .map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />
                          {s.replace(/^•\s*/, '')}
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            )}
        </SectionCard>

        {/* Multimedia Resources */}
        {displayLesson.multimediaResources && (
          <SectionCard colorKey="formative" heading="Multimedia Resources" {...sp('multimediaResources')}>
            {isEditing
              ? <EditableField label="Multimedia Resources" value={editableLesson.multimediaResources ?? ''}
                  onChange={v => setEditableLesson(prev => prev ? { ...prev, multimediaResources: v } : prev)} rows={8} />
              : <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{displayLesson.multimediaResources}</p>}
          </SectionCard>
        )}

        {/* Extension Activity */}
        <SectionCard colorKey="ext" heading="Extension Activity" {...sp('extensionActivity')}>
          {isEditing
            ? <EditableField label="Extension Activity" value={editableLesson.extensionActivity}
                onChange={v => setEditField('extensionActivity', v)} rows={3} />
            : <p className="text-sm text-slate-700 leading-relaxed">{displayLesson.extensionActivity}</p>}
        </SectionCard>

        {/* Homework */}
        <SectionCard colorKey="hw" heading="Homework" {...sp('homework')}>
          {isEditing
            ? <EditableField label="Homework" value={editableLesson.homework}
                onChange={v => setEditField('homework', v)} rows={3} />
            : <p className="text-sm text-slate-700 leading-relaxed">{displayLesson.homework}</p>}
        </SectionCard>

        {/* Teacher Reflection Notes */}
        <SectionCard colorKey="reflect" heading="Teacher Reflection Notes" {...sp('reflectionNotes')}>
          {isEditing
            ? <EditableField label="Reflection prompts (one per line)" value={editableLesson.reflectionNotes}
                onChange={v => setEditField('reflectionNotes', v)} rows={5} />
            : <BulletList text={displayLesson.reflectionNotes} />}
        </SectionCard>

        {/* Bottom action bar */}
        <div className="flex flex-wrap items-center gap-2 pb-8 pt-2 print:hidden">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={() => { setEditableLesson({ ...lesson }); setIsEditing(false); }}
                className="rounded-xl border-slate-200 text-slate-600 gap-1.5">
                <XCircle size={14} /> Cancel
              </Button>
              <div className="flex-1" />
              <Button size="sm" onClick={handleDoneEditing}
                className="rounded-xl bg-primary hover:bg-primary/90 text-white gap-1.5">
                <CheckCircle2 size={15} /> Done Editing
              </Button>
            </>
          ) : (
            <>
              {/* Create from buttons */}
              <Button variant="outline" size="sm"
                onClick={() => handleCreateFrom('activity-sheets', 'Activity Sheet')}
                className="rounded-xl border-slate-200 hover:border-violet-300 hover:bg-violet-50 text-slate-600 hover:text-violet-700 gap-1.5">
                <FileText size={14} /> Create Activity Sheet
              </Button>
              <Button variant="outline" size="sm"
                onClick={() => handleCreateFrom('presentations', 'Presentation')}
                className="rounded-xl border-slate-200 hover:border-amber-300 hover:bg-amber-50 text-slate-600 hover:text-amber-700 gap-1.5">
                <Monitor size={14} /> Create Presentation
              </Button>
              <Button variant="outline" size="sm"
                onClick={() => handleCreateFrom('assessments', 'Assessment')}
                className="rounded-xl border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-slate-600 hover:text-emerald-700 gap-1.5">
                <ClipboardList size={14} /> Create Assessment
              </Button>
              <Button variant="outline" size="sm" onClick={handleSave}
                className={`rounded-xl gap-1.5 ${savedResourceId ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}>
                {savedResourceId ? <CheckCircle2 size={15} /> : <Save size={15} />}
                {savedResourceId ? 'Saved' : 'Save to My Resources'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleStartNew}
                className="rounded-xl border-slate-200 hover:border-slate-300 text-slate-600 gap-1.5">
                <PlusCircle size={15} /> Start New Lesson
              </Button>
            </>
          )}
        </div>

        {/* Post-save CTAs */}
        {savedResourceId && !isEditing && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="pb-4 flex flex-col items-center gap-2 print:hidden">
            {onOpenWorkspace && (
              <button
                onClick={() => onOpenWorkspace(savedResourceId)}
                className="flex items-center gap-2 text-sm font-semibold bg-primary text-white hover:bg-primary/90 px-4 py-2 rounded-xl shadow-sm transition-colors"
              >
                Open Lesson Workspace →
              </button>
            )}
            <button onClick={onGoMyResources}
              className="text-sm text-slate-500 hover:text-slate-800 transition-colors">
              View in My Resources
            </button>
          </motion.div>
        )}
      </motion.div>
      </LessonThemeContext.Provider>
    </>
  );
}

// Helper: parse duration string to minutes
function t(pct: number, duration: string): number {
  return Math.round(parseInt(duration) * pct);
}
